import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getProfileApi, updateProfileApi, uploadProfilePictureApi, getProfilePictureApi } from '../../profile';

/**
 * Consolidated test component for all Profile endpoints
 */
const TestProfile: React.FC = () => {
  // Get Profile state
  const [getUserId, setGetUserId] = useState('');
  const [getResponse, setGetResponse] = useState<any>(null);
  const [getLoading, setGetLoading] = useState(false);
  const [getError, setGetError] = useState<string | null>(null);

  // Update Profile state
  const [updateUserId, setUpdateUserId] = useState('');
  const [updateFirstName, setUpdateFirstName] = useState('');
  const [updateLastName, setUpdateLastName] = useState('');
  const [updateDisplayName, setUpdateDisplayName] = useState('');
  const [updateBio, setUpdateBio] = useState('');
  const [updateResponse, setUpdateResponse] = useState<any>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Upload Profile Picture state
  const [uploadUserId, setUploadUserId] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadResponse, setUploadResponse] = useState<any>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Get Profile Picture state
  const [getPictureUserId, setGetPictureUserId] = useState('');
  const [getPictureResponse, setGetPictureResponse] = useState<any>(null);
  const [getPictureLoading, setGetPictureLoading] = useState(false);
  const [getPictureError, setGetPictureError] = useState<string | null>(null);

  // Handlers
  const handleGetProfile = async () => {
    setGetLoading(true);
    setGetError(null);

    try {
      const result = await getProfileApi(getUserId.trim() || undefined);
      setGetResponse(result);
    } catch (err) {
      setGetError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setGetLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!updateUserId.trim()) {
      setUpdateError('Please enter a user ID');
      return;
    }

    setUpdateLoading(true);
    setUpdateError(null);

    try {
      const request: any = {
        userId: updateUserId.trim(),
      };
      if (updateFirstName.trim()) request.firstName = updateFirstName.trim();
      if (updateLastName.trim()) request.lastName = updateLastName.trim();
      if (updateDisplayName.trim()) request.displayName = updateDisplayName.trim();
      if (updateBio.trim()) request.bio = updateBio.trim();

      const result = await updateProfileApi(request);
      setUpdateResponse(result);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!uploadUserId.trim() || !uploadFile) {
      setUploadError('Please enter user ID and select a file');
      return;
    }

    setUploadLoading(true);
    setUploadError(null);

    try {
      const result = await uploadProfilePictureApi(uploadFile);
      setUploadResponse(result);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleGetProfilePicture = async () => {
    if (!getPictureUserId.trim()) {
      setGetPictureError('Please enter a user ID');
      return;
    }

    setGetPictureLoading(true);
    setGetPictureError(null);

    try {
      const result = await getProfilePictureApi(getPictureUserId.trim());
      setGetPictureResponse(result);
    } catch (err) {
      setGetPictureError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setGetPictureLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
        Profile APIs
      </Typography>

      {/* Get Profile */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Get Profile</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="User ID (Optional)"
              value={getUserId}
              onChange={(e) => setGetUserId(e.target.value)}
              fullWidth
              placeholder="Leave empty for current user"
              helperText="Get your own profile by leaving this empty, or enter a user ID to view another user's profile"
            />
            <Button
              variant="contained"
              onClick={handleGetProfile}
              disabled={getLoading}
            >
              {getLoading ? <CircularProgress size={24} /> : 'Get Profile'}
            </Button>
            {getResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(getResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {getError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{getError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Update Profile */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Update Profile</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="User ID"
              value={updateUserId}
              onChange={(e) => setUpdateUserId(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="First Name"
              value={updateFirstName}
              onChange={(e) => setUpdateFirstName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={updateLastName}
              onChange={(e) => setUpdateLastName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Display Name"
              value={updateDisplayName}
              onChange={(e) => setUpdateDisplayName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Bio"
              value={updateBio}
              onChange={(e) => setUpdateBio(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <Button
              variant="contained"
              onClick={handleUpdateProfile}
              disabled={updateLoading}
            >
              {updateLoading ? <CircularProgress size={24} /> : 'Update Profile'}
            </Button>
            {updateResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(updateResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {updateError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{updateError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Upload Profile Picture */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Upload Profile Picture</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="User ID"
              value={uploadUserId}
              onChange={(e) => setUploadUserId(e.target.value)}
              fullWidth
              required
            />
            <Button
              variant="outlined"
              component="label"
            >
              Select Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </Button>
            {uploadFile && (
              <Typography variant="body2">
                Selected: {uploadFile.name} ({uploadFile.size} bytes)
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleUploadProfilePicture}
              disabled={uploadLoading}
            >
              {uploadLoading ? <CircularProgress size={24} /> : 'Upload Profile Picture'}
            </Button>
            {uploadResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(uploadResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {uploadError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{uploadError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Get Profile Picture */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Get Profile Picture</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="User ID"
              value={getPictureUserId}
              onChange={(e) => setGetPictureUserId(e.target.value)}
              fullWidth
              required
            />
            <Button
              variant="contained"
              onClick={handleGetProfilePicture}
              disabled={getPictureLoading}
            >
              {getPictureLoading ? <CircularProgress size={24} /> : 'Get Profile Picture'}
            </Button>
            {getPictureResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Profile Picture URL:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    mb: 2
                  }}
                >
                  {getPictureResponse.profilePictureURL}
                </Typography>
                {getPictureResponse.profilePictureURL && (
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mb: 2 }}
                      onClick={() => window.open(getPictureResponse.profilePictureURL, '_blank')}
                    >
                      Open Image
                    </Button>
                    <Box
                      component="img"
                      src={getPictureResponse.profilePictureURL}
                      alt="Profile"
                      sx={{ maxWidth: '200px', maxHeight: '200px', borderRadius: 1 }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </Box>
                )}
              </Paper>
            )}
            {getPictureError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{getPictureError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TestProfile;
